import { ChoiceItem } from '@ingka/choice';
import { useAppDispatch } from 'ducks/hooks';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import SkapaChoice from 'ui/Choice/Choice';
import { isMobile } from 'utils/platform';
import FreeStandingIcon from './icons/free-standing.png';
import WallMountedIcon from './icons/wall-mounted.png';
import { usePlannerConfig } from 'contexts/plannerConfig';
import { getSelectedRange, setHiddenProductGroups, setSelectedRange } from 'ducks/range';
import { useEffect, useMemo } from 'react';
import Box from 'ui/Box';
import media from 'ui/media';
import { useCore } from 'contexts/core';
import { useServiceSettings } from 'contexts/serviceSettings';
import { getMarketProducts } from 'ducks/products';
import { ProductType } from '@inter-ikea-storageone/range';
import store from 'ducks/configureStore';
import SkapaTabs from 'ui/Tabs/SkapaTabs';
import { Tab } from '@ingka/tabs';

const Wrapper = styled.div`
  margin-top: ${({ theme }) => (isMobile() ? 0 : theme.space.xl)};
`;

const MobileWrapper = styled(Box)`
  padding-left: 1.5rem;
  padding-right: 1.5rem;
  padding-bottom: 0.5rem;
  ${media.thinMobile`
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  `}
`;

const StyledSkapaChoice = styled(SkapaChoice)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 20px;
  align-items: stretch;
`;

type ChoiceOption = {
  id: string;
  title: string;
  image: { src: string; alt: string };
  onSelect: () => void;
};

const shouldBeRendered = () => {
  const BROR_WALL_PRODUCT_TYPES = [
    ProductType.BROR_WALL_SECTION_UNIT,
    ProductType.BROR_SHELF_WALL_METAL_BLACK,
    ProductType.BROR_RAIL,
    ProductType.BROR_PEGBOARD_SET
  ];

  const productsInMarket = getMarketProducts(store.getState());
  const wallProductsInMarket = productsInMarket.filter(p =>
    BROR_WALL_PRODUCT_TYPES.includes(p.type)
  );

  return wallProductsInMarket.length > 0;
};

const ChoiceChildren = (options: ChoiceOption[]) => {
  return options.map(option => (
    <ChoiceItem
      id={option.id}
      data-testid={option.id}
      title={option.title}
      image={{ src: option.image?.src, alt: option.title }}
      imagePosition="trailing"
      onClick={() => option.onSelect()}
      key={option.id}
      style={{ height: '100%', justifyContent: 'center' }}
    />
  ));
};

export default function RangeToggle() {
  const dispatch = useAppDispatch();
  const { plannerConfig } = usePlannerConfig();
  const currentRange = useSelector(getSelectedRange);
  const { formatMessage } = useIntl();
  const coreApi = useCore();
  const { rangeName } = useServiceSettings();

  const rangeChoiceOptions = useMemo(() => {
    const wallMountedProductGroups = [
      'wall-sections',
      'wall-interiors',
      'wall-pegboards',
      'wall-organizers'
    ];
    return [
      {
        id: 'bror-free-standing',
        title: rangeName,

        image: { src: FreeStandingIcon, alt: rangeName },
        onSelect: () => {
          const hiddenProductGroups = plannerConfig.productGroups
            .filter(group => wallMountedProductGroups.includes(group.id))
            .map(group => group.id);
          dispatch(setSelectedRange('bror-free-standing'));
          dispatch(setHiddenProductGroups(hiddenProductGroups));
        }
      },
      {
        id: 'bror-wall-mounted',
        title: `${rangeName} ${formatMessage({ id: 'storageone.wallSystem' })}`,
        image: {
          src: WallMountedIcon,
          alt: `${rangeName} ${formatMessage({ id: 'storageone.wallSystem' })}`
        },
        onSelect: () => {
          const hiddenProductGroups = plannerConfig.productGroups
            .filter(group => !wallMountedProductGroups.includes(group.id))
            .map(group => group.id);
          dispatch(setSelectedRange('bror-wall-mounted'));
          dispatch(setHiddenProductGroups(hiddenProductGroups));
        }
      }
    ];
  }, [dispatch, formatMessage, plannerConfig.productGroups, rangeName]);
  useEffect(() => {
    if (currentRange === '') {
      rangeChoiceOptions[0].onSelect();
    }
  });

  useEffect(() => {
    const reset = () => {
      rangeChoiceOptions[0].onSelect();
    };
    return coreApi.subscribe('reset', reset);
  }, [coreApi, rangeChoiceOptions]);

  const selectedIndex = rangeChoiceOptions.findIndex(option => option.id === currentRange) || 0;

  if (!shouldBeRendered()) return null;

  return isMobile() ? (
    <MobileWrapper>
      {rangeChoiceOptions && (
        <SkapaTabs
          defaultActiveTab={rangeChoiceOptions[0].id}
          onTabChanged={tabId => rangeChoiceOptions.find(option => option.id === tabId)?.onSelect()}
          tabs={rangeChoiceOptions.map(option => {
            return (
              <Tab
                key={option.id}
                tabPanelId={option.id}
                data-testid={`range-${option.id}`}
                text={formatMessage({ id: option.title })}
              />
            );
          })}
        />
      )}
    </MobileWrapper>
  ) : (
    <Wrapper>
      <StyledSkapaChoice
        children={ChoiceChildren(rangeChoiceOptions)}
        preselectedIndex={selectedIndex}
      />
    </Wrapper>
  );
}
